import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { Avatar } from '@thoughtspot/radiant-react/widgets/avatar';
import { Divider } from '@thoughtspot/radiant-react/widgets/divider';
import { Link } from '@thoughtspot/radiant-react/widgets/link';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Popover } from '@thoughtspot/radiant-react/widgets/popover';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import _ from 'lodash';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Key } from 'w3c-keys';
import styles from './user-menu.module.scss';
const NotificationsContainer = React.lazy(() => import('./notifications/notifications.container'));
const AvatarWithInAppContainer = React.lazy(() => import('./avatar-with-in-app-container'));
export var UserMenuTestIds;
(function (UserMenuTestIds) {
    UserMenuTestIds["USER_PROFILE_AVATAR"] = "user-profile-avatar";
    UserMenuTestIds["USER_MENU_POPOVER"] = "user-menu-popover";
    UserMenuTestIds["USER_PROFILE_AVATAR_WITH_IN_APP"] = "user-profile-avatar-in-app";
})(UserMenuTestIds || (UserMenuTestIds = {}));
export const UserMenu = ({ collectEvent, getUserDisplayName, getUserGuid, goToUserPreference, handleLogout, hasProfilePic, isInAppNotificationEnabled, updateMessages, inviteTeammates, onMenuClose, notificationsData, isCopilotEnabled = false, }) => {
    const { t } = useTranslation();
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [avatarKey, setAvatarKey] = useState(null);
    const profilePicRef = useRef(null);
    const profileSectionRef = useRef(null);
    const resetMessages = () => {
        if (isInAppNotificationEnabled) {
            updateMessages({
                updateMessagesToUnread: true,
            });
        }
    };
    const resetUserMenuState = () => {
        setShowProfileMenu(false);
        profilePicRef.current.focus();
        resetMessages();
        if (_.isFunction(onMenuClose)) {
            onMenuClose();
        }
    };
    const handleKeyDown = (event) => {
        if (event?.key === Key.Escape && showProfileMenu === true) {
            resetUserMenuState();
        }
    };
    useEffect(() => {
        setAvatarKey(hasProfilePic ? 'profilePic' : 'noProfilePic');
    }, [hasProfilePic]);
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);
    useEffect(() => {
        if (showProfileMenu) {
            profileSectionRef?.current?.focus();
        }
    }, [showProfileMenu]);
    const avatarSrc = `/callosum/v1/image/profile/${getUserGuid()}`;
    const commonAvatarProps = {
        name: getUserDisplayName(),
        src: avatarSrc,
        namePosition: 'bottom',
        showTooltip: false,
    };
    const seeYourProfile = () => {
        collectEvent(new UserAction(userActionTypeEnum.CLICK_VIEW_PROFILE), {});
        setShowProfileMenu(false);
        goToUserPreference();
    };
    const getUserMenuContent = () => {
        const displayName = getUserDisplayName();
        const ellipsis = { rows: 0 };
        return (React.createElement("div", { className: styles.profileDropDown, tabIndex: 0, role: "button" },
            React.createElement("div", { className: styles.profileSection, ref: profileSectionRef, tabIndex: 0, role: "tablist" },
                React.createElement("div", { tabIndex: 0, role: "tab", "aria-label": t('profile.userPicture') },
                    React.createElement(Avatar, { key: avatarKey, name: getUserDisplayName(), src: avatarSrc, namePosition: "bottom", showTooltip: false, className: styles.userMenuAvatar })),
                React.createElement("div", { className: styles.infoSection, role: "tablist" },
                    React.createElement("div", { tabIndex: 0, role: "tab", "aria-label": `${displayName}` },
                        React.createElement(Typography, { variant: "content-label-subhead", "aria-level": 2, ellipsis: ellipsis, wrapContent: true }, displayName)),
                    React.createElement("div", { className: styles.profileAndLogout, role: "tab" },
                        React.createElement("div", { role: "link" },
                            React.createElement(Link, { className: styles.profileLink, onClick: seeYourProfile, tabIndex: 0, "aria-label": t('profile.dropdown.Profile'), onKeyDown: (e) => {
                                    if (e.key === Key.Enter ||
                                        e.key === Key.Space) {
                                        seeYourProfile();
                                    }
                                } }, t('profile.dropdown.Profile'))),
                        React.createElement("div", { className: styles.verticalRod }),
                        React.createElement("div", { role: "link" },
                            React.createElement(Link, { className: styles.logoutLink, "aria-label": t('profile.dropdown.SIGN_OUT'), onClick: () => {
                                    setShowProfileMenu(false);
                                    handleLogout();
                                }, tabIndex: 0, onKeyDown: (e) => {
                                    if (e.key === Key.Enter ||
                                        e.key === Key.Space) {
                                        setShowProfileMenu(false);
                                        handleLogout();
                                    }
                                } }, t('profile.dropdown.SIGN_OUT')))))),
            React.createElement(React.Fragment, null, inviteTeammates),
            !isCopilotEnabled && isInAppNotificationEnabled && (React.createElement(React.Fragment, null,
                React.createElement(Divider, { className: styles.divider }),
                React.createElement(NotificationsContainer, { resetMessages: resetMessages, closeProfileMenu: () => setShowProfileMenu(false), shouldFetchMessages: showProfileMenu })))));
    };
    return (React.createElement(Suspense, { fallback: React.createElement(LoadingIndicator.Contextual, { className: styles.loadingIndicator }) },
        React.createElement(Popover, { className: styles.profilePopover, bottomOffset: 8, targetRef: profilePicRef, content: getUserMenuContent(), isVisible: showProfileMenu, align: "bottom-start", appendToBody: true, onClickOutside: () => {
                resetUserMenuState();
            }, "data-testid": UserMenuTestIds.USER_MENU_POPOVER }),
        React.createElement("button", { type: "button", "aria-label": t('USER_PROFILE_HEADER', {
                nameOfUser: getUserDisplayName(),
            }), tabIndex: 0, ref: profilePicRef, className: styles.avatarButton, onClick: () => {
                if (showProfileMenu) {
                    resetUserMenuState();
                }
                else {
                    setShowProfileMenu(true);
                }
            }, "aria-expanded": !!showProfileMenu }, !isInAppNotificationEnabled ? (React.createElement(Avatar, { ...commonAvatarProps, key: avatarKey, "data-testid": UserMenuTestIds.USER_PROFILE_AVATAR })) : (React.createElement(AvatarWithInAppContainer, { ...commonAvatarProps, notificationsData: notificationsData, key: avatarKey, "data-testid": UserMenuTestIds.USER_PROFILE_AVATAR_WITH_IN_APP })))));
};
//# sourceMappingURL=user-menu.js.map